import Eventable from 'utils/eventable';
import session from './session';

/**
 * Parse SVP Player events and tailor data to pulse requirements
 *
 * Available events: Play, Pause, Stop, Watch
 */
class PlaybackEvents extends Eventable {
    constructor(player) {
        super();
        this.player = player;
        this.watchData = {
            time: 0
        };

        this.session = this.createSession();
        this.beforePlaybackStarted = true;
        this.beforeUnload = ['beforeunload', this.onBeforeUnload.bind(this), true];

        this.listenTo(this.player, 'playlistItem', this.onPlaylistItem, this);
        this.listenTo(this.player, 'initialPlay', this.onInitialPlay, this);
        this.listenTo(this.player, 'assetPlay', this.onAssetPlay, this);
        this.listenTo(this.player, 'pause', this.onPause, this);
        this.listenTo(this.player, 'play', this.onPlay, this);
        this.listenTo(this.player, 'complete', this.onComplete, this);

        this.listenTo(this.player, 'time', this.onTime, this);

        this.listenTo(
            this.player,
            'RecommendedPlugin:nextAssetPlay',
            this.onRecommendedPlay.bind(this, 'recommendedNext')
        );

        this.listenTo(
            this.player,
            'RecommendedPlugin:playRecommended',
            this.onRecommendedPlay.bind(this, 'recommendedGrid')
        );

        window.addEventListener(...this.beforeUnload);
    }

    get eventData() {
        const data = {};
        const previousAssetId = this.session.previousAssetId;

        if(previousAssetId) {
            data.previousAssetId = previousAssetId;
            data.playbackSource = this.session.playbackSource;
        }

        try {
            data.position = Math.floor(this.player.getCurrentTime());
        } catch (e) {}

        return data;
    }

    getPosition() {
        try {
            return Math.floor(this.player.getCurrentTime());
        } catch (e) {
            return 0;
        }
    }

    createSession() {
        // node is unique player identifier
        const node = this.player.config.get('node');
        const id = node.id || node;

        /**
         * Initialize session object to keep reference
         */
        if(!session[id]) {
            session[id] = {};
        }

        return session[id];
    }

    onBeforeUnload() {
        if(this.beforeStreamStart === false) {
            this.onComplete();
        }
    }

    onInitialPlay() {
        this.beforePlaybackStarted = false;
    }

    onPlaylistItem() {
        let playaheadTime = 0;

        try {
            playaheadTime = this.player.model.getStream().getPlaybackTime('begin') || 0;
        } catch (e) {}


        const track = this.trigger.bind(this, 'Load', Object.assign({
            position: playaheadTime
        }, this.eventData));

        /**
         * Only first playback has to wait for viewable event
         * Playnext/grid will work without it
         */
        if(this.session.wasViewed === true) {
            track();
        } else {
            // wait for player to be viewable
            this.listenTo(this.player.model.player, 'viewable', (data) => {
                if(data.viewable) {
                    track();
                    this.stopListening(this.player.model.player, 'viewable');
                    this.session.wasViewed = true;
                }
            });
        }

        this.previousAssetId = null;
    }

    onAssetPlay() {
        this.beforeStreamStart = false;

        this.trigger('Play', Object.assign({
            start: true
        }, this.eventData));
    }

    onPause() {
        this.trigger('Pause', this.eventData);
    }

    onPlay() {
        // play can't be triggered before assetPlay
        if(this.beforeStreamStart === false) {
            this.trigger('Play', this.eventData);
        }
    }

    onTime(eventTime) {
        const time = Math.floor(eventTime);
        let eventInterval = 1;

        /**
         * Intervals are defined as follow
         *
         * every 1s - during the first 10 seconds of content playback (no matter of where playback has started
         * every 5s - for rest of content playback, after seeking intervals should be triggered in same time
         * seek 42, then first trigger should be in 45
         */
        if(time >= 10) {
            eventInterval = 5;
        }

        /**
         * Tracking is submitted in different intervals
         * onTime event is triggered multiple times per second, to prevent this we check last time of event triggered
         */
        if(time % eventInterval === 0 && this.watchData.time !== time) {
            this.watchData.time = time;

            this.trigger('Watch', Object.assign(this.eventData, {
                position: time
            }));
        }
    }

    onComplete(reason) {
        this.beforeStreamStart = true;

        if(reason === 'playNext') {
            this.onRecommendedPlay('playNext');
        }

        /**
         * Track stop event only when playback has started
         */
        if(this.beforePlaybackStarted === false) {
            this.trigger('Stop', this.eventData);

            // re-attach stop event after replay has been clicked
            // this listener is cleaned in destroy() when stream has changed
            this.listenToOnce(this.player, 'play', () => {
                this.onAssetPlay();
            });
        }
    }

    onRecommendedPlay(playbackSource) {
        // store asset id in memory to attach it to play next video
        this.session.previousAssetId = this.player.getAsset().get('id');
        this.session.playbackSource = playbackSource;
    }

    destroy() {
        window.removeEventListener(...this.beforeUnload);
        this.stopListening();
    }
}

export default PlaybackEvents;